import { useState, useEffect, useRef, useCallback } from 'react';
import { startCollaborationSession, connectToWebSocket } from '../../../services/collaboration.service';

const getToken = () => {
    const docplace = localStorage.getItem('docplace');
    return docplace ? `Bearer ${JSON.parse(docplace).token}` : null;
};

/**
 * @param {string} formId - Form request id
 * @param {function} onFieldChanged - (fieldId, fieldValue, userId) => void
 * @param {string} flowId - Flow id for the session
 * @param {function} [onFlowCompleted] - (payload) => void. Called when WebSocket receives flow_completed (flowId, message, timestamp).
 */
export const useFormCollaboration = (formId, onFieldChanged, flowId = "", onFlowCompleted = null) => {
    const [isConnected, setIsConnected] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [error, setError] = useState(null);
    const [isFlowCompleted, setIsFlowCompleted] = useState(false);
    const socketRef = useRef(null);
    const formIdRef = useRef(formId);
    const flowIdRef = useRef(flowId);
    const userIdRef = useRef(null);
    const onFieldChangedRef = useRef(onFieldChanged);
    const onFlowCompletedRef = useRef(onFlowCompleted);
    const hasInitialized = useRef(false);

    useEffect(() => {
        onFieldChangedRef.current = onFieldChanged;
    }, [onFieldChanged]);

    useEffect(() => {
        onFlowCompletedRef.current = onFlowCompleted;
    }, [onFlowCompleted]);

    useEffect(() => {
        flowIdRef.current = flowId;
    }, [flowId]);

    useEffect(() => {
        if (!formId || !flowId || hasInitialized.current) return;

        hasInitialized.current = true;
        console.log('[WS] Initializing collaboration:', { formId, flowId });

        const initializeCollaboration = async () => {
            try {
                console.log('[WS] Starting collaboration session...');
                const sessionData = await startCollaborationSession(formId, flowId);
                console.log('[WS] Session data received:', sessionData);

                setSessionId(sessionData.session_id);
                formIdRef.current = sessionData.form_id;
                userIdRef.current = sessionData.user_id;

                console.log('[WS] Connecting to WebSocket...');
                const socket = connectToWebSocket();
                socketRef.current = socket;

                socket.onopen = () => {
                    console.log('[WS] Socket opened');
                    setIsConnected(true);

                    const message = {
                        type: 'connect_session',
                        formId: sessionData.form_id,
                        userId: sessionData.user_id,
                        flowId: flowIdRef.current
                    };

                    console.log('[WS] Sending connect_session message:', message);
                    socket.send(JSON.stringify(message));
                };

                socket.onmessage = (event) => {
                    console.log('--- [WS] NEW MESSAGE RECEIVED ---');
                    console.log('[WS] Raw message data:', event.data);
                    try {
                        const data = JSON.parse(event.data);
                        console.log('[WS] Parsed message:', data);

                        if (data.type === 'field_changed' && onFieldChangedRef.current) {
                            console.log('[WS] Field changed event received, applying change:', {
                                fieldId: data.fieldId,
                                fieldValue: data.fieldValue,
                                userId: data.userId
                            });
                            onFieldChangedRef.current(data.fieldId, data.fieldValue, data.userId);
                        } else if (data.type === 'flow_completed') {
                            const matchesFlow = !data.flowId || data.flowId === flowIdRef.current;
                            if (matchesFlow) {
                                console.log('[WS] Flow completed event received:', { flowId: data.flowId, message: data.message });
                                setIsFlowCompleted(true);
                                if (onFlowCompletedRef.current) {
                                    onFlowCompletedRef.current({ flowId: data.flowId, message: data.message, timestamp: data.timestamp });
                                }
                            }
                        }
                    } catch (err) {
                        console.error('[WS] Error parsing WebSocket message:', err);
                    }
                };

                socket.onclose = (event) => {
                    console.log('[WS] Socket closed:', event.code, event.reason);
                    setIsConnected(false);
                };

                socket.onerror = (err) => {
                    console.error('[WS] Socket error:', err);
                    setError(err);
                };

            } catch (err) {
                console.error('[WS] Error initializing collaboration:', err);
                setError(err);
            }
        };

        initializeCollaboration();

        return () => {
            console.log('[WS] Cleaning up WebSocket connection');
            if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                socketRef.current.close();
            }
        };
    }, [formId, flowId]);

    const sendFieldUpdate = useCallback((fieldId, fieldValue) => {
        if (socketRef.current &&
            socketRef.current.readyState === WebSocket.OPEN &&
            isConnected &&
            formIdRef.current) {
            const authToken = getToken();
            const message = {
                type: 'edit_input_field',
                formId: formIdRef.current,
                flowId: flowIdRef.current,
                fieldId,
                fieldValue,
                userId: userIdRef.current,
                token: authToken,
                sessionId
            };
            console.log('[WS] Sending field update:', message);
            socketRef.current.send(JSON.stringify(message));
        }
    }, [isConnected, sessionId]);

    return {
        isConnected,
        sessionId,
        error,
        isFlowCompleted,
        socket: socketRef.current,
        sendFieldUpdate
    };
};
